import {FC} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import FormResult from 'components/Form/FormResult';
import InputHidden from 'components/Form/InputHidden';
import Radio from 'components/Form/Radio';
import SubmitButton from 'components/Form/SubmitButton';
import TextArea from 'components/Form/TextArea';
import Information from 'components/Information';
import Stacked from 'components/Layout/Stacked';
import Link from 'components/Link';
import LinkButton from 'components/LinkButton';
import {useBackend} from 'state/backend';
import {Backend} from 'types/api';
import {FormFields} from 'types/utils';
import {setUrl} from 'utils/request';
import {onApiFormError} from 'utils/request/result';
import {BillingCancelRequest, useBillingCancel} from './services';

export type ProfileCancelConfirmBackend = Backend<{
    partnerMid: string;
}>;

export type ProfileCancelConfirmFormData = FormFields<BillingCancelRequest>;

const CancelConfirm: FC = () => {
    const {data} = useBackend<ProfileCancelConfirmBackend>();

    const methods = useForm<ProfileCancelConfirmFormData>();
    const {handleSubmit} = methods;

    const {mutateAsync} = useBillingCancel({partnerMid: data.partnerMid});
    const {t} = useTranslation('Billing.CancelConfirm');

    const onSubmit = async (formData: ProfileCancelConfirmFormData) => {
        await mutateAsync(formData)
            .then(() => setUrl(window.Urls.billing_index()))
            .catch(onApiFormError(methods));
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Stacked className="max-w-xl">
                    <Information title={t('warning')} type="alert">
                        <ul>
                            <li>
                                <Trans
                                    components={[
                                        <Link
                                            key={0}
                                            href="https://help.makeleaps.jp/ja/articles/5614653"
                                            isOpenInNewTab={true}
                                        />,
                                    ]}
                                    i18nKey="warning1"
                                    ns="Billing.CancelConfirm"
                                    t={t}
                                />
                            </li>
                        </ul>
                        <ul>
                            <li>
                                <Trans
                                    components={[
                                        <Link
                                            key={0}
                                            href="https://www.makeleaps.jp/pricing/"
                                            isOpenInNewTab={true}
                                        />,
                                    ]}
                                    i18nKey="warning2"
                                    ns="Billing.CancelConfirm"
                                    t={t}
                                />
                            </li>
                        </ul>
                    </Information>
                    <div>{t('ifYouAgreeToTheseRestrictions')}</div>
                    <InputHidden name="cancel" value="yes" />
                    <Radio
                        label={t('reason.selectedLabel')}
                        name="selected_reason"
                        options={[
                            {
                                label: t('reason.option1'),
                                value: 'no_need_for_doc_management',
                            },
                            {
                                label: t('reason.option2'),
                                value: 'switched_to_competitor',
                            },
                            {
                                label: t('reason.option3'),
                                value: 'lack_of_feature',
                            },
                            {
                                label: t('reason.option4'),
                                value: 'bad_user_experience',
                            },
                            {
                                label: t('reason.option5'),
                                value: 'expensive',
                            },
                            {
                                label: t('reason.option6'),
                                value: 'other',
                            },
                        ]}
                        required={t('form:required')}
                    />
                    <TextArea
                        aria-label="reason_detail"
                        label={t('reasonDetails')}
                        name="reason_detail"
                        required={t('form:required')}
                        rows={4}
                    />
                    <TextArea
                        aria-label="improvements"
                        label={t('improvements')}
                        name="improvements"
                        rows={4}
                    />
                    <FormResult />
                    <div className="flex justify-end">
                        <LinkButton className="mr-8" href="/" kind="tertiary">
                            {t('form:cancel')}
                        </LinkButton>
                        <SubmitButton kind="destructive">
                            {t('form:confirm')}
                        </SubmitButton>
                    </div>
                </Stacked>
            </form>
        </FormProvider>
    );
};

export default CancelConfirm;
